import { useEffect, useRef } from 'react';

// Port coordinates for map display
const PORT_COORDS = {
  CNSHA: [31.2304, 121.4737],
  CNNGB: [29.8683, 121.5440],
  CNYTN: [22.5431, 114.0579],
  HKHKG: [22.3193, 114.1694],
  SGSIN: [1.2644, 103.8222],
  KRPUS: [35.1028, 129.0403],
  JPTYO: [35.6762, 139.6503],
  USLA:  [33.7395, -118.2601],
  USLGB: [33.7701, -118.1937],
  USNYC: [40.6892, -74.0445],
  USSAV: [32.0835, -81.0998],
  NLRTM: [51.9244, 4.4777],
  DEHAM: [53.5511, 9.9937],
  GBFXT: [51.9536, 1.3513],
  ESALG: [36.7213, -2.9109],
  AEJEA: [25.0185, 55.0808],
  INBOM: [18.9388, 72.8354],
  LKCMB: [6.9422, 79.8428],
  MYPKG: [2.9927, 101.3874],
  BRSSZ: [-23.9536, -46.3322]
};

const TrackingMap = ({ originPort, destinationPort, currentLat, currentLng, currentLocation }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Check if Leaflet is available
    if (!window.L || !mapRef.current) return;

    const L = window.L;

    // Clean up existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }

    const originCoords = PORT_COORDS[originPort];
    const destCoords = PORT_COORDS[destinationPort];

    if (!originCoords || !destCoords) {
      return;
    }

    // Create map centered between origin and destination
    const centerLat = (originCoords[0] + destCoords[0]) / 2;
    const centerLng = (originCoords[1] + destCoords[1]) / 2;

    const map = L.map(mapRef.current).setView([centerLat, centerLng], 3);
    mapInstanceRef.current = map;

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18
    }).addTo(map);

    // Origin marker (green)
    L.circleMarker(originCoords, {
      radius: 8,
      fillColor: '#28a745',
      color: '#fff',
      weight: 2,
      fillOpacity: 0.9
    }).addTo(map).bindPopup(`Origin: ${originPort}`);

    // Destination marker (red)
    L.circleMarker(destCoords, {
      radius: 8,
      fillColor: '#dc3545',
      color: '#fff',
      weight: 2,
      fillOpacity: 0.9
    }).addTo(map).bindPopup(`Destination: ${destinationPort}`);

    // Route line
    L.polyline([originCoords, destCoords], {
      color: '#6c757d',
      weight: 2,
      dashArray: '5, 10',
      opacity: 0.6
    }).addTo(map);

    // Current position marker (blue)
    if (currentLat && currentLng) {
      const lat = parseFloat(currentLat);
      const lng = parseFloat(currentLng);

      L.circleMarker([lat, lng], {
        radius: 10,
        fillColor: '#007bff',
        color: '#fff',
        weight: 3,
        fillOpacity: 0.9
      }).addTo(map).bindPopup(
        `Current Position${currentLocation ? `: ${currentLocation}` : ''}`
      );

      // Draw path from origin to current position
      L.polyline([originCoords, [lat, lng]], {
        color: '#007bff',
        weight: 3,
        opacity: 0.8
      }).addTo(map);
    }

    // Fit map to show all markers
    const bounds = [originCoords, destCoords];
    if (currentLat && currentLng) {
      bounds.push([parseFloat(currentLat), parseFloat(currentLng)]);
    }
    map.fitBounds(bounds, { padding: [30, 30] });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [originPort, destinationPort, currentLat, currentLng, currentLocation]);

  const originCoords = PORT_COORDS[originPort];
  const destCoords = PORT_COORDS[destinationPort];

  if (!originCoords || !destCoords) {
    return (
      <div className="tracking-map-container">
        <div className="map-unavailable">
          Map unavailable - port coordinates not found for {originPort} or {destinationPort}
        </div>
      </div>
    );
  }

  // Check if Leaflet is loaded
  if (!window.L) {
    return (
      <div className="tracking-map-container">
        <div className="map-unavailable">
          Map requires Leaflet library. Add Leaflet CSS and JS to your index.html:
          <br />
          <code>&lt;link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" /&gt;</code>
          <br />
          <code>&lt;script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"&gt;&lt;/script&gt;</code>
        </div>
      </div>
    );
  }

  return (
    <div className="tracking-map-container">
      <div ref={mapRef} className="tracking-map" />
    </div>
  );
};

export default TrackingMap;
